import {
  PortfolioDiversificationIdNumber,
  UNSET_DIVERSIFICATION_CATEGORY_ID,
} from '@/utils/enums';

/**
 * Pure helpers for `getAccountDiversification` in `diversificationsApi` so request shape and
 * cache identity can be unit-tested without a live RTK store.
 */

type AccountDiversificationQueryArgs = {
  accountId: number;
  asOfDate?: string;
  diversificationId?: number;
  categoryId?: number | null;
  /**
   * When true for root-only requests (`diversificationId` is the portfolio root and no valid `categoryId`),
   * the API is asked to group by sub-classification.
   */
  groupBySubClassification?: boolean;
};

/**
 * GET params sent with `/api/investor/AccountDiversification/:accountId`.
 * Grouping keys are only applied for the account root slice, not for drilldown levels.
 */
export function buildAccountDiversificationRequestParams(
  args: AccountDiversificationQueryArgs,
): Record<string, string | number> {
  const diversificationId =
    args.diversificationId ?? PortfolioDiversificationIdNumber.ROOT;
  const categoryId =
    args.categoryId != null && args.categoryId > 0 ? args.categoryId : null;
  const isRootRequest =
    diversificationId === PortfolioDiversificationIdNumber.ROOT &&
    categoryId == null;
  const shouldGroupRoot =
    Boolean(args.groupBySubClassification) && isRootRequest;

  return {
    accountId: args.accountId,
    diversificationId,
    ...(categoryId != null ? { categoryId } : {}),
    ...(args.asOfDate ? { asOfDate: args.asOfDate } : {}),
    ...(shouldGroupRoot
      ? {
          'group.ValueProperty': 'SubClassificationId',
          'group.DisplayProperty': 'SubClassificationName',
        }
      : {}),
  };
}

/** RTK Query cache tag `id` for account diversification (must stay aligned with `providesTags`). */
export function getAccountDiversificationCacheTagId(
  args: Pick<
    AccountDiversificationQueryArgs,
    'accountId' | 'diversificationId' | 'categoryId'
  >,
): string {
  return `account-${args.accountId}-${args.diversificationId ?? PortfolioDiversificationIdNumber.ROOT}-${args.categoryId ?? UNSET_DIVERSIFICATION_CATEGORY_ID}`;
}
